import React, { useState } from "react";
import {
  Box,
  Typography,
  Badge,
  Menu,
  List,
  ListItem,
  ListItemText,
  Divider,
  Button,
  CircularProgress,
  Chip
} from "@mui/material";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import {
  get_notifications,
  clear_notifications,
  mark_notification_read,
} from "../../api/ControllApi";
import { useNavigate } from "react-router-dom";
import { useUser } from "../../context/LoginDetail";
import { useModal } from "../../context/ModalContext";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);

const ensureArray = (data) => {
  if (Array.isArray(data)) return data;
  if (!data) return [];
  if (typeof data === "object") {
    const items = data.notifications || data.items || data;
    if (Array.isArray(items)) return items;
    const values = Object.values(items);
    if (values.length > 0 && (items[Object.keys(items)[0]]?.id || items[0]?.id)) {
      return values;
    }
  }
  return [];
};

const cleanMessage = (msg) => {
  if (!msg) return "";
  return msg
    .replace(/&#8377;/g, "₹")
    .replace(/\u00e2\u201a\u00b9/g, "₹")
    .replace(/&nbsp;/g, " ");
};

const NotificationDropdown = () => {
  const { user, notificationCount, fetchNotificationCount } = useUser();
  const { setShowLogin } = useModal();
  const navigate = useNavigate();

  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const [clearing, setClearing] = useState(false);

  const isLoggedIn = !!user;
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    if (!isLoggedIn) {
      setShowLogin(true);
      return;
    }
    setAnchorEl(event.currentTarget);
    fetchLatestNotifications();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const fetchLatestNotifications = async () => {
    setLoading(true);
    try {
      const res = await get_notifications(1);
      if (res.status) {
        setNotifications(ensureArray(res.data));
      }
    } catch (err) {
      console.error("Failed to fetch notifications", err);
    } finally {
      setLoading(false);
    }
  };

  const handleClearAll = async () => {
    try {
      setClearing(true);
      const res = await clear_notifications();
      if (res.status) {
        setNotifications((prev) =>
          prev.map((n) => ({ ...n, is_read: 1 }))
        );
        if (fetchNotificationCount) {
          fetchNotificationCount();
        }
      }
    } catch (err) {
      console.error("Failed to clear notifications", err);
    } finally {
      setClearing(false);
    }
  };

  const handleNotificationClick = async (notification) => {
    handleClose();
    if (!notification.is_read) {
      try {
        await mark_notification_read(notification.id);
        if (fetchNotificationCount) {
          fetchNotificationCount();
        }
      } catch (err) {
        // silent fail
      }
    }
    navigate("/dashboard/notifications");
  };

  const count = notificationCount || 0;

  return (
    <Box>
      {/* Bell Button styled exactly per design */}
      <Box
        onClick={handleClick}
        sx={{
          bgcolor: "#EAF4EC",
          width: 38,
          height: 38,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: "pointer",
          position: "relative",
          transition: "all 0.2s ease",
          "&:hover": { bgcolor: "#DDF0E1", transform: "scale(1.05)" },
        }}
        title={isLoggedIn ? "Notifications" : "Login to view notifications"}
      >
        <Badge
          variant={count > 9 ? "standard" : "dot"}
          badgeContent={count > 9 ? count : undefined}
          invisible={!isLoggedIn || count <= 0}
          sx={{
            "& .MuiBadge-badge": {
              bgcolor: "#FF5757",
              color: "#fff",
              fontSize: "0.65rem",
              height: count > 9 ? 16 : 8,
              minWidth: count > 9 ? 16 : 8,
              borderRadius: "50%",
              px: count > 9 ? 0.5 : 0,
            },
          }}
        >
          <NotificationsNoneIcon sx={{ color: "#163F1D", fontSize: "1.2rem" }} />
        </Badge>
      </Box>

      {/* Dropdown Popover Panel */}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          sx: {
            width: { xs: "92vw", sm: 375 },
            maxHeight: 500,
            mt: 1.5,
            boxShadow: "0px 10px 35px rgba(0,0,0,0.12)",
            borderRadius: 3,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            border: "1px solid #E5E7EB",
            "& .MuiList-root": { p: 0 },
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
        disableScrollLock
      >
        {/* Header */}
        <Box
          sx={{
            p: 2,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            bgcolor: "#F8FAF8",
            borderBottom: "1px solid #EDF2EE",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="h6" sx={{ fontSize: "0.95rem", fontWeight: 800, color: "#163F1D" }}>
              Notifications
            </Typography>
            {count > 0 && (
              <Chip
                label={`${count} new`}
                size="small"
                sx={{
                  bgcolor: "#FF5757",
                  color: "#fff",
                  fontWeight: 700,
                  fontSize: "0.7rem",
                  height: 20,
                }}
              />
            )}
          </Box>
          {notifications.some((n) => !n.is_read) && (
            <Button
              size="small"
              onClick={handleClearAll}
              disabled={clearing}
              startIcon={<DoneAllIcon sx={{ fontSize: "1rem !important" }} />}
              sx={{
                textTransform: "none",
                fontSize: "0.75rem",
                fontWeight: 600,
                color: "#166534",
                p: 0.5,
                "&:hover": { bgcolor: "#EAF4EC" },
              }}
            >
              Mark all read
            </Button>
          )}
        </Box>

        {/* Content list */}
        <Box sx={{ flexGrow: 1, maxHeight: 340, overflowY: "auto" }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 6 }}>
              <CircularProgress size={26} sx={{ color: "#166534" }} />
            </Box>
          ) : notifications.length === 0 ? (
            <Box sx={{ py: 6, px: 3, textAlign: "center" }}>
              <Box
                sx={{
                  width: 52,
                  height: 52,
                  borderRadius: "50%",
                  bgcolor: "#EAF4EC",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 1.5,
                }}
              >
                <NotificationsNoneIcon sx={{ color: "#166534", fontSize: "1.6rem" }} />
              </Box>
              <Typography variant="body2" sx={{ fontWeight: 700, color: "#374151" }}>
                No notifications yet
              </Typography>
              <Typography variant="caption" sx={{ color: "#9CA3AF" }}>
                We'll notify you when important updates arrive.
              </Typography>
            </Box>
          ) : (
            <List sx={{ py: 0 }}>
              {notifications.slice(0, 8).map((notification, index) => {
                const isUnread = !notification.is_read || notification.is_read === 0;
                return (
                  <React.Fragment key={notification.id || index}>
                    <ListItem
                      button
                      onClick={() => handleNotificationClick(notification)}
                      sx={{
                        px: 2,
                        py: 1.5,
                        alignItems: "flex-start",
                        bgcolor: isUnread ? "#F0FDF4" : "#FFFFFF",
                        borderLeft: isUnread ? "3px solid #16a34a" : "3px solid transparent",
                        transition: "background-color 0.15s",
                        "&:hover": { bgcolor: "#F4FAF5" },
                      }}
                    >
                      <ListItemText
                        primary={
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.8, mb: 0.3 }}>
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: isUnread ? 700 : 500,
                                fontSize: "0.85rem",
                                color: isUnread ? "#111827" : "#374151",
                              }}
                            >
                              {notification.title || "Notification"}
                            </Typography>
                            {isUnread && (
                              <Box
                                sx={{
                                  width: 6,
                                  height: 6,
                                  borderRadius: "50%",
                                  bgcolor: "#16a34a",
                                }}
                              />
                            )}
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: "0.8rem",
                                color: "#4B5563",
                                lineHeight: 1.4,
                                mb: 0.5,
                                wordBreak: "break-word",
                              }}
                            >
                              {cleanMessage(notification.message)}
                            </Typography>
                            <Typography variant="caption" sx={{ color: "#9CA3AF", fontSize: "0.72rem" }}>
                              {dayjs(notification.created_at).fromNow()}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                    {index < notifications.length - 1 && <Divider component="li" />}
                  </React.Fragment>
                );
              })}
            </List>
          )}
        </Box>

        {/* Footer */}
        <Box
          sx={{
            p: 1.2,
            textAlign: "center",
            borderTop: "1px solid #EDF2EE",
            bgcolor: "#F8FAF8",
          }}
        >
          <Button
            fullWidth
            size="small"
            endIcon={<ArrowForwardIcon sx={{ fontSize: "1rem !important" }} />}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              fontSize: "0.82rem",
              color: "#166534",
              py: 0.8,
              borderRadius: 2,
              "&:hover": { bgcolor: "#EAF4EC" },
            }}
            onClick={() => {
              handleClose();
              navigate("/dashboard/notifications");
            }}
          >
            View all notifications
          </Button>
        </Box>
      </Menu>
    </Box>
  );
};

export default NotificationDropdown;
